import {Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CustomerAlarmModalComponent} from './customer-alarm-modal/customer-alarm-modal.component';
import {TariffDto, CustomerAlarmSubscriptionDto, CustomerAlarmSubscriptionService} from '../../../_services/configuration-services';
import {Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {faPen, faToggleOff, faToggleOn, faTrash} from '@fortawesome/free-solid-svg-icons';
import {NotificationsService} from '../../../_shared/notifications.service';
import {DateUtils} from '../../../_shared/date-utils';
import {TranslateUtils} from '../../../_shared/translate-utils';

@Component({
    selector: 'app-customer-alarm-amanagement',
    templateUrl: './customer-alarm-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerAlarmManagementComponent implements OnInit, OnDestroy {

    @Input() serviceId: string;
    @Input() serviceActive: boolean;
    @Input() tariff: TariffDto | null;

    @ViewChild('subscriptionTable') table;

    subscriptions: CustomerAlarmSubscriptionDto[] = [];

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    displayDateFormat = DateUtils.dateFormat;

    // Icons
    icons = {
        edit: faPen,
        delete: faTrash,
        toggleOn: faToggleOn,
        toggleOff: faToggleOff
    };

    // Used to display expiration date in terminate modal
    terminateModalContent = {
        terminationDate: null
    };

    constructor(private readonly modalService: NgbModal,
                private readonly translateService: TranslateService,
                private readonly notificationsService: NotificationsService,
                private readonly customerAlarmSubscriptionService: CustomerAlarmSubscriptionService) { }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe(async (event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.fetchAlarmSubscriptions();
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
        this.modalService.dismissAll('destroy');
    }

    fetchAlarmSubscriptions(): void {
        this.customerAlarmSubscriptionService.getMySubscriptions().pipe().subscribe(subscriptions => {
            this.subscriptions = subscriptions;
        });
    }

    addAlarmSubscription(): void {
        const modal = this.modalService.open(CustomerAlarmModalComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.tariff = this.tariff;

        modal.result.then(_ => {
            this.fetchAlarmSubscriptions();
        }, () => { /* catch the rejection */ });
    }

    editSubscription(subscription: CustomerAlarmSubscriptionDto): void {
        const modal = this.modalService.open(CustomerAlarmModalComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.tariff = this.tariff;
        modal.componentInstance.customerAlarmSubscription = subscription;

        modal.result.then(_ => {
            this.fetchAlarmSubscriptions();
        }, () => { /* catch the rejection */ });
    }

    activateSubscription(subscription: CustomerAlarmSubscriptionDto): void {
        this.customerAlarmSubscriptionService.activateSubscription(subscription.id).pipe().subscribe(_ => {
            this.notificationsService.success({title: 'services.customerAlarm.subscriptions.notifications.activateSuccess'});
            this.fetchAlarmSubscriptions();
        });
    }

    deactivateSubscription(subscription: CustomerAlarmSubscriptionDto): void {
        this.customerAlarmSubscriptionService.deactivateSubscription(subscription.id).pipe().subscribe(_ => {
            this.notificationsService.success({title: 'services.customerAlarm.subscriptions.notifications.deactivateSuccess'});
            this.fetchAlarmSubscriptions();
        });
    }

    terminateSubscription(subscription: CustomerAlarmSubscriptionDto, terminateSubscriptionConfirmModal): void {

        this.customerAlarmSubscriptionService.getSubscriptionTerminationDate(subscription.id).pipe().subscribe(terminationDate => {
            this.terminateModalContent.terminationDate = terminationDate;

            const modal = this.modalService.open(terminateSubscriptionConfirmModal, {centered: true});
            modal.result
                .then(value => {
                    if (value === 'confirm') {
                        this.customerAlarmSubscriptionService.terminateSubscription(subscription.id, subscription).pipe().subscribe(_ => {
                            this.notificationsService.success({title: 'services.customerAlarm.subscriptions.notifications.terminateSuccess'});
                            this.fetchAlarmSubscriptions();
                        });
                    }
                }, () => { /* catch the rejection */ });
        });
    }
}
